// src/features/activity/DecisionsTab.tsx
// The decision journal — what was decided, and why. ListDecisions, RecordDecision.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { ListDecisions } from '@core/api/endpoints/activity/list_decisions';
import type { Activity } from '@core/types/activity';
import { RecordDecisionDialog } from './RecordDecisionDialog';
import { relativeTime, errMessage } from './logic';

interface DecisionsTabProps {
  onOpenActivity: (activity: Activity) => void;
  selectedId?: string;
}

export const DecisionsTab = observer(({ onOpenActivity, selectedId }: DecisionsTabProps) => {
  const { t } = useI18n();
  const [items, setItems] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    ListDecisions({ limit: 50 })
      .then((res) => setItems(res.decisions ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('activity.decisions.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('activity.decisions.subtitle')}</p>
        <Button size="sm" onClick={() => setFormOpen(true)}>{t('activity.decisions.record')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {loading && items.length === 0 && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}

      {!loading && items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('activity.decisions.empty')}</p>
        </div>
      )}

      <ul className="divide-y divide-border rounded-md border border-border">
        {items.map((a) => {
          const id = a.entity?.id ?? '';
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => onOpenActivity(a)}
                className={`flex w-full items-start justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted ${selectedId === id ? 'bg-muted/50' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{a.summary}</p>
                  <p className="text-xs text-muted-foreground">{a.actor?.display_name || t('common.unknown')}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{relativeTime(a.created_at as unknown as string)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <RecordDecisionDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
});

export default DecisionsTab;
